import { useState } from 'react';
import { useAutomationReplyIntents } from '@/hooks/use-automation-replies';
import type { DayRange } from '@/lib/automation-replies';
import type { SettingsColumnHead } from '@/modules/settings';
import type { AutomationCheckStatus, AutomationOutcome } from '@/services/automation-reply.service';

export type AutomationOutcomeFilter = 'all' | AutomationOutcome;

export const OUTCOME_OPTIONS: { value: AutomationOutcomeFilter; label: string }[] = [
	{ value: 'all', label: 'All' },
	{ value: 'sent', label: 'Sent' },
	{ value: 'blocked', label: 'Not sent' }
];

export type AutomationChecksFilter = 'all' | AutomationCheckStatus;

export const CHECKS_OPTIONS: { value: AutomationChecksFilter; label: string }[] = [
	{ value: 'all', label: 'All checks' },
	{ value: 'fail', label: 'Checks failed' },
	{ value: 'pass', label: 'Checks passed' },
	{ value: 'no_trace', label: 'No trace' }
];

/** Radix Select takes no empty value, so "every intent" has a key of its own. */
export const ALL_INTENTS = 'all';

/** The Automation log's column titles, rendered in the page's sticky header. */
export const AUTOMATION_LOG_COLUMNS: SettingsColumnHead[] = [
	{ id: 'time', header: 'Time', className: 'w-[160px]' },
	{ id: 'helpdesk', header: 'Helpdesk', className: 'w-[110px]' },
	{ id: 'channel', header: 'Channel', className: 'w-[96px]' },
	{ id: 'ticket', header: 'Ticket ID', className: 'w-[96px]' },
	{ id: 'review', header: 'Review', className: 'w-[130px]' },
	{ id: 'intent', header: 'Intent', className: 'w-[136px]' },
	{ id: 'message', header: 'AI automated response' },
	{ id: 'status', header: 'Status', className: 'w-[104px]' },
	{ id: 'confidence', header: 'Confidence', className: 'w-[104px]' }
];

const GAP_COLUMN: SettingsColumnHead = { id: 'gap', header: 'Gap', className: 'w-[72px]' };

/** The log's columns; the Gap column only for a user who can open the gap board. */
export const automationLogColumns = (canViewGaps: boolean): SettingsColumnHead[] =>
	canViewGaps ? [...AUTOMATION_LOG_COLUMNS, GAP_COLUMN] : AUTOMATION_LOG_COLUMNS;

/** The search box and filters of the Automation log; kept by the Logs page so its header can show them. */
export function useAutomationLogFilters() {
	const [query, setQuery] = useState('');
	const [days, setDays] = useState<DayRange>({});
	const [outcome, setOutcome] = useState<AutomationOutcomeFilter>('all');
	const [chosenIntent, setIntent] = useState(ALL_INTENTS);
	const [checks, setChecks] = useState<AutomationChecksFilter>('all');
	const intents = useAutomationReplyIntents();
	// After a brand switch, an intent the new brand's log never names reads as every intent.
	const intent = intents.includes(chosenIntent) ? chosenIntent : ALL_INTENTS;
	return { query, setQuery, days, setDays, outcome, setOutcome, intents, intent, setIntent, checks, setChecks };
}

export type AutomationLogFilters = ReturnType<typeof useAutomationLogFilters>;
